import { Box, Stack, TextField, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { createCategoryReq, updateCategoryReq } from "@domain/changelog"
import type { CategoryTranslations } from "@models/changelog"
import { showErrorToast, showSuccessToast } from "@utils/toast"

import { langFlag, langLabel } from "../shared"

const COLORS = [
  "#4F46E5",
  "#1F9254",
  "#D64545",
  "#E0A020",
  "#157CFC",
  "#8B5CF6",
  "#DB2777",
  "#0E9AA7"
]

export default function CategoryDialog() {
  const {
    translate,
    settings,
    isCategoryModalOpen,
    editingCategory,
    closeCategoryModal,
    refetchCategories
  } = useChangelogPageContext()

  const supportedLanguages = useMemo(
    () => settings?.supportedLanguages ?? ["tr", "en"],
    [settings]
  )

  const [names, setNames] = useState<Record<string, string>>({})
  const [color, setColor] = useState(COLORS[0])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isCategoryModalOpen) {
      return
    }
    if (editingCategory) {
      const next: Record<string, string> = {}
      supportedLanguages.forEach((lang) => {
        next[lang] = editingCategory.translations?.[lang]?.name || ""
      })
      setNames(next)
      setColor(editingCategory.color || COLORS[0])
    } else {
      setNames({})
      setColor(COLORS[0])
    }
  }, [isCategoryModalOpen, editingCategory, supportedLanguages])

  const canSave = supportedLanguages.every((lang) => !!names[lang]?.trim())

  const onSave = async () => {
    if (!canSave) {
      showErrorToast(translate("changelog_category_names_required"))
      return
    }
    setLoading(true)
    try {
      const translations: CategoryTranslations = {}
      supportedLanguages.forEach((lang) => {
        translations[lang] = { name: names[lang].trim() }
      })
      const res = editingCategory
        ? await updateCategoryReq({
            _id: editingCategory._id,
            color,
            translations
          })
        : await createCategoryReq({ color, translations })
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || translate("changelog_save_failed"))
        return
      }
      showSuccessToast(translate("changelog_category_saved"))
      refetchCategories()
      closeCategoryModal()
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogProvider
      title={
        editingCategory
          ? translate("changelog_edit_category")
          : translate("changelog_new_category")
      }
      isOpen={isCategoryModalOpen}
      setClose={closeCategoryModal}
      size="small"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: closeCategoryModal
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: onSave,
        loading,
        disabled: !canSave
      }}
    >
      <Box>
        {supportedLanguages.map((lang) => (
          <Box key={lang}>
            <Label text={`${langFlag(lang)} ${langLabel(lang)}`} />
            <TextField
              fullWidth
              size="small"
              value={names[lang] || ""}
              onChange={(e) =>
                setNames((prev) => ({ ...prev, [lang]: e.target.value }))
              }
              placeholder={translate("changelog_category_name_ph")}
              sx={{ mb: 2 }}
            />
          </Box>
        ))}

        <Label text={translate("changelog_category_color")} />
        <Stack direction="row" gap={1}>
          {COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => setColor(c)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: c,
                cursor: "pointer",
                border: color === c ? "3px solid" : "3px solid transparent",
                borderColor: color === c ? "rgba(0,0,0,0.25)" : "transparent",
                boxShadow: color === c ? `0 0 0 2px ${c}` : "none"
              }}
            />
          ))}
        </Stack>
      </Box>
    </DialogProvider>
  )
}

function Label({ text }: { text: string }) {
  return (
    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, mb: 0.75 }}>
      {text}
    </Typography>
  )
}
